/**
 * This file is for pure functions that operate on other fee data, telling you
 * additional information about them.
 */
import type { TAssignedFeeData } from '@/modules/api/get/assignedFees.ts'
import { useStore } from '@/stores'
import { isFormSubmissionId } from '@/modules/util/formSubmissionId'

export const shouldAutoAdd = (fee: TAssignedFeeData, studentAccountId: TStudentAccountId) => {
  const store = useStore()
  const student = store.getStudent(studentAccountId)
  // DTA-949: someone else is paying for this coupon book (a payment link, or
  // money sent to school), so it is not the family's to pay by default.
  if ((fee as TAssignedFeeData).couponBookWaiting) {
    return false
  }
  return (
    ((!fee.disabled && !fee.variablePricingEnabled && !hasUnsignedForms(fee)) ||
      (fee.enrollmentId != null && !fee.variablePricingEnabled && !hasUnsignedForms(fee))) &&
    !(student.onHold && fee.onHoldAllowedToPurchase === 2)
  )
}

/**
 * Structural rather than tied to TAssignedFeeData: the identical rule has to hold
 * for Incident IQ and PowerSchool fees, which are separate types carrying the same
 * two fields. One definition, so the modules cannot drift apart again — that drift
 * is what left IncidentIqFeesSection on `=== null` while the others were hardened.
 */
type TFormGatedFee = Pick<TAssignedFeeData, 'formData' | 'formSubmissionId'>

export const hasUnsignedForms = (fee: TFormGatedFee) => {
  // DTA-659: a stored "0" or "" is not a submission. Testing `=== null` here
  // meant a junk value read as "form already signed", so the fee was silently
  // auto-added to the cart on dashboard load — and checkout then rejected the
  // whole order over a line the family never chose and could not see a form
  // button for. 753 production fees carry exactly that value.
  return fee.formData !== null && !isFormSubmissionId(fee.formSubmissionId)
}
